import { Component, ViewChild } from '@angular/core';
import { AccessManager } from '../../components/ui/access-manager/access-manager';
import {
  TextInputComponent,
  TextModes,
} from '../../components/ui/controls/text-input/text-input.component';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { UIHelpers } from '../../core/ui-helpers';
import { StorageService } from '../../core/storage/storageService';
import { CustomerService } from '../../models/customer/customerService';
import { InvoiceService } from '../../models/invoices/invoiceService';
import { PaymentMethod } from '../../models/paymentMethod/paymentMethod';
import { PaymentMethodService } from '../../models/paymentMethod/paymentMethodService';
import { RecurringSchedule } from '../../models/recurringPayments/recurringSchedule';
import { StreetAddress } from '../../models/streetAddress/streetAddress';
import { TerminalTransaction } from '../../models/transactions/transaction';
import { TerminalTransactionRequest } from '../../models/orders/terminalTransactionRequest';
import { TransactionFacade } from '../../models/transactions/transactionFacade';
import { TransactionResponse } from '../../models/transactions/transactionResponse';
import { TransactionService } from '../../models/transactions/transactionService';
import { Customer } from '../../models/customer/customer';
import { TerminalBillingFormComponent } from './terminal-billing-form/terminal-billing-form.component';
import { TerminalCustomerComponent } from './terminal-customer/terminal-customer.component';
import { TerminalTransactionsComponent } from './terminal-transactions/terminal-transactions.component';
import { BasePage } from '../BasePage';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderDetails } from '../../models/orders/orderDetails';
import { TerminalOrderComponent } from './terminal-order/terminal-order.component';
import { TerminalL2L3Component } from './terminal-L2L3/terminal-L2L3.component';
// import { L2L3Data } from '../../models/orders/L2L3Data';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { AdminService } from '../../models/admin/adminService';
import { TransactionType, TransactionTypeVerb } from '../../core/constants';
import { IconType } from '../../../../../../libs/Shared/components/icon/icon';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { TerminalShippingFormComponent } from './terminal-shipping-form/terminal-shipping-form.component';
import { L2L3Item } from '../../models/orders/L2L3Item';

enum PaneEnum {
  Transactions,
  Product,
  Billing,
  OrderDetails,
  L2L3,
  Shipping,
  RecurringPayment,
  Refund,
  Customer,
  Summary,
}

@Component({
  selector: 'app-terminal',
  templateUrl: './terminal.component.html',
  standalone: false,
})
export class TerminalComponent extends BasePage {
  @ViewChild(TextInputComponent) txtTotal: TextInputComponent | undefined;
  @ViewChild(TerminalBillingFormComponent) billingForm:
    | TerminalBillingFormComponent
    | undefined;
  @ViewChild(TerminalTransactionsComponent) transactionsForm:
    | TerminalTransactionsComponent
    | undefined;
  @ViewChild(TerminalCustomerComponent) customerForm:
    | TerminalCustomerComponent
    | undefined;
  @ViewChild(TerminalOrderComponent) orderDetailsForm:
    | TerminalOrderComponent
    | undefined;
  @ViewChild(TerminalL2L3Component) L2L3Form: TerminalL2L3Component | undefined;
  @ViewChild(TerminalShippingFormComponent) shippingForm: TerminalShippingFormComponent | undefined;

  namespace = AccessManager.AUTH_INSERT_MERCHANT_TRANSACTIONS;

  testDDL: string = '';

  allowTransactions = true;
  merchantId: string;
  achEnabled: boolean = false;
  inputTextModes = TextModes;
  transactionTypeOptions: ListItem[] = [];
  tokenizeInterval: number | undefined;
  preExistingTransaction: boolean = false;
  allowTransactionTypeSelection: boolean = true;
  allowChargeAmountChange: boolean = true;

  IconType = IconType;

  // TO DO: pull this from the db with the payment method, I guess
  static MAGNIFIER: number = 100;
  static CURRENCY_CODE: string = 'USD';

  selectedMode: number | undefined;
  selectedTransactionFriendlyName: string = '';
  selectedCustomer: Partial<Customer> | undefined;
  previousCustomer: Partial<Customer> | undefined; // for restoring to previous view state
  selectedPaymentMethod: Partial<PaymentMethod> | undefined;
  selectedBillingAddress: Partial<StreetAddress> = StreetAddress.createEmpty();
  shippingAddress: Partial<StreetAddress> = StreetAddress.createEmpty();
  orderDetails: Partial<OrderDetails> = OrderDetails.createEmpty();

  // Transactions
  previousTransactions: TransactionFacade[] | undefined;
  selectedTransaction: Partial<TerminalTransaction> | undefined;
  subsequentTransactions: any[] | undefined;
  refundRemaining: boolean = false;

  // Validation
  paymentFormPassesValidation: boolean = false;
  formPassesValidation: boolean = false;
  formIsComplete: boolean = false;

  // This is what will actually be submitted for the transaction as verififed by the user
  paymentMethodId: string | undefined;
  amountToTransact: number | undefined;
  orderId: string | undefined;
  maxRefundAmount: number | undefined;

  formIsBeingSubmitted: boolean = false;
  transactionResponse: TransactionResponse | undefined;

  // UI
  MODE_CREDIT_CARD = 1;
  MODE_ECHECK = 2;
  MODE_INVOICE = 3;

  TransactionType = TransactionType;

  paymentTypeOptions: ListItem[] = [];
  shippingOptions: ListItem[] = [new ListItem({ id:'none', name: 'No Shipping Address' }), new ListItem({ id:'show', name: 'Include Shipping Address' })];

  selectedShippingMode: string = 'none';
  shippingPaneExpanded: boolean = false;
  level2Level3PaneExpanded: boolean = false;
  transactionPaneExpanded: boolean = true;

  loadingSpinnerRef: MatDialogRef<LoadingSpinnerComponent> | undefined;

  constructor(
    private route: ActivatedRoute,
    public storage: StorageService,
    public customerService: CustomerService,
    public transactionService: TransactionService,
    public invoiceService: InvoiceService,
    public paymentMethodService: PaymentMethodService,
    public uihelper: UIHelpers,
    private uiErrorHandler: UIErrorHandler,
    private router: Router,
    public loadingSpinnerDialog: MatDialog,
    private adminService: AdminService,
  ) {
    super();

    let merchant = this.storage.getSelectedLocation();

    this.paymentTypeOptions.push(new ListItem({ id: this.MODE_CREDIT_CARD.toString(), name: 'Credit Card' }));
    this.paymentTypeOptions.push(new ListItem({ id: this.MODE_ECHECK.toString(), name: 'Electronic Check (ACH)' }));

    if (merchant && merchant.id) {
      this.merchantId = merchant.id;

      for (let processor of merchant.processors) {
        if (processor.processorType == 'ACH') {
          this.achEnabled = true;
        }
      }
    } else {
      // TODO: redirect to merchants page?
      throw 'no merchant selected';
    }

    this.transactionTypeOptions.push(
      new ListItem({
        id: TransactionType.TRANSACTION_TYPE_AUTHCAPTURE.toString(),
        name: 'Sale',
      }),
    );
    this.transactionTypeOptions.push(
      new ListItem({
        id: TransactionType.TRANSACTION_TYPE_AUTH.toString(),
        name: 'Authorize',
      }),
    );
    this.adminService
      .getFeatureFlagByName('recurringPayments')
      .then((isEnabled) => {
        if (isEnabled) {
          this.transactionTypeOptions.push(
            new ListItem({
              id: TransactionType.TRANSACTION_TYPE_RECUR.toString(),
              name: 'Recurring',
            }),
          );
        }
      });
  }

  override async ngOnInit() {
    super.ngOnInit();

    let that = this;

    this.route.paramMap.subscribe((p) => {
      let tid = p.get('tid');
      let verb = p.get('v');

      if (tid) {
        this.transactionService
          .getTransaction(tid)
          .then((t) => {
            if (t) {
              //Need to get what kind of payment method was used.
              if (t.cardType == 'other'){
                that.selectedMode = this.MODE_ECHECK;
              } else {
                that.selectedMode = this.MODE_CREDIT_CARD;
              }

              that.selectedTransaction = t;
              that.preExistingTransaction = true;
              that.orderDetails.transactionType = parseInt(verb?.toString() || '-1');
              that.allowTransactionTypeSelection = false;
              that.allowChargeAmountChange = false;

              if (
                that.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_REFUND
              ) {
                that.allowChargeAmountChange = true;
                that.maxRefundAmount = that.uihelper.demagnifyAmount(
                  t.maxRefundAmount,
                  t.magnifier!,
                );
              }
              that.customerService
                .getCustomerById(t.customerId)
                .then((c) => {
                  that.selectedCustomer = c;
                  that.customerForm?.setCustomer(c);
                })
                .catch((message) => {
                  that.uiErrorHandler.handleError('Customer not found');
                });

              that.paymentMethodService
                .getPaymentMethod(t.paymentMethodId, t.customerId)
                .then((pm) => {
                  that.selectedPaymentMethod = pm;
                  that.billingForm?.setPaymentMethod(pm.id, t.customerId);
                })
                .catch((message) => {
                  that.uiErrorHandler.handleError('Payment method not found');
                });

              if (t.magnifiedAmount && t.magnifier && t.currencyCode) {
                if (
                  that.orderDetails.transactionType ==
                    TransactionType.TRANSACTION_TYPE_REFUND &&
                  t.magnifiedAmount != t.maxRefundAmount
                ) {
                  that.amountToTransact = TransactionFacade.demagnifyAmount(
                    t.maxRefundAmount!,
                    t.magnifier!,
                  );
                  that.orderDetails.totalAmount = that.amountToTransact;

                } else {
                  that.amountToTransact = TransactionFacade.demagnifyAmount(
                    t.magnifiedAmount,
                    t.magnifier,
                  );
                  that.orderDetails.totalAmount = that.amountToTransact;

                }
              } else {
                that.uiErrorHandler.handleError('Transaction amount not found');
                return;
              }

              that.checkIfFormIsComplete();
            }
          })
          .catch((message) => {
            that.uiErrorHandler.handleError('Transaction not found');
          });
      } else {
        this.orderDetails.transactionType = TransactionType.TRANSACTION_TYPE_AUTHCAPTURE;
        this.selectTransType_click(TransactionType.TRANSACTION_TYPE_AUTHCAPTURE);
      }
    });
  }

  ngOnDestroy() {
    if (this.tokenizeInterval) {
      window.clearInterval(this.tokenizeInterval);
    }
  }

  changeRefundAmount(amount: number) {
    this.amountToTransact = amount;
    this.orderDetails.totalAmount = amount;
    this.checkIfFormIsComplete();
  }

  txtChargeAmount_keyup(amountToTransact: number|undefined) {
    if (amountToTransact) {
      this.orderDetails.totalAmount = amountToTransact;
      this.handleChargeAmountChanged(amountToTransact);
    }
    else {
      this.orderDetails.totalAmount = 0;
    }
    this.checkIfFormIsComplete();
  }

  handleTokenizeFormValidationChanged(data: any) {
    this.paymentFormPassesValidation = data.formPassedValidation;

    // this.selectedPaymentMethod = PaymentMethod.createEmpty();
    // this.selectedPaymentMethod.cardExpMonth = data.cardData.mm;
    // this.selectedPaymentMethod.cardExpYear = data.cardData.yy;
    // this.selectedPaymentMethod.cardFirst6 = data.cardData.first6;
    // this.selectedPaymentMethod.cardLast4 = data.cardData.last4;

    this.checkIfFormIsComplete();
  }

  handleSelectedPaymentMethodChanged(paymentMethod: Partial<PaymentMethod>) {
    if (paymentMethod) {
      if (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTH &&
        (paymentMethod.cardType == 'checking' ||
          paymentMethod.cardType == 'savings')
      ) {
        this.uiErrorHandler.handleError(
          'ACH accounts are not supported for authorization transactions',
        );
        this.billingForm?.clear();
        this.billingForm?.clearSelection();
        return;
      }
      this.selectedPaymentMethod = paymentMethod;
      this.paymentMethodId = paymentMethod.id;
      this.billingForm?.clearSelection();
    }

    this.checkIfFormIsComplete();
  }

  handleSelectedCustomerChanged(customer: Partial<Customer>) {
    this.selectedCustomer = customer;
    this.selectedPaymentMethod = undefined;
    this.paymentMethodId = undefined;
    this.billingForm?.refresh();
    this.billingForm?.clear();
    this.checkIfFormIsComplete();
  }

  handlePaymentTypeChanged() {
    if (this.selectedMode == this.MODE_ECHECK) {
      this.billingForm!.selectedPaymentType = this.MODE_ECHECK;
      this.billingForm!.achEnabled = true;
    } else {
      this.billingForm!.selectedPaymentType = this.MODE_CREDIT_CARD;
      this.billingForm!.achEnabled = false;
    }
    this.billingForm!.refresh();
    this.billingForm!.loadPaymentMethods();
    this.checkIfFormIsComplete();
  }

  handleRecurringUpdate(_schedule: RecurringSchedule) {
    if (this.orderDetails.recurringSchedule) {
      this.orderDetails.recurringSchedule.nextChargeDate =
        this.orderDetails.recurringSchedule.firstChargeDate;
    }
    this.checkIfFormIsComplete();
  }

  handleAddressUpdated(address: Partial<StreetAddress>) {
    this.selectedBillingAddress = address;
  }

  handleShippingAddressUpdated(address: Partial<StreetAddress>) {
    this.shippingAddress = address;
  }

  handleOrderDetailsUpdated(details: Partial<OrderDetails>) {
    this.orderDetails = details;
  }

  handleChargeAmountChanged(amount: number) {
    this.amountToTransact = amount;
    this.checkIfFormIsComplete();
  }

  handleTokenized(paymentMethodId: string) {
    this.paymentMethodId = paymentMethodId;
    this.selectedPaymentMethod = PaymentMethod.createEmpty();
    this.selectedPaymentMethod.id = paymentMethodId;
    this.checkIfFormIsComplete();
  }

  btnNewTransaction_click() {
    if (this.preExistingTransaction) {
      this.router.navigate(['merchants/terminal']);
    } else {
      this.clearAllSelections();
    }
  }

  btnCompleteTransaction_click() {
    // If the user entered new card info, we first send a message
    // to the iframe to tokenize.  Upon completion, we'll finish
    // the transaction.  Since we are reliant on brower messaging
    // to communicate with the iframe, our listener will handle
    // the response, which will then be detected in the Interval.

    this.formIsBeingSubmitted = true;

    this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
      LoadingSpinnerComponent,
      {
        disableClose: true,
        panelClass: 'transparent-dialog',
        data: {
          mode: 'indeterminate',
        },
      },
    );

    if (this.selectedShippingMode == 'none') {
      this.shippingForm?.clearShippingAddress();
    } // clear possibler hidden shipping address if no shipping address is selected

    if (!this.selectedPaymentMethod || !this.selectedPaymentMethod.id) {
      this.billingForm?.tokenize();

      let that = this;

      this.tokenizeInterval = window.setInterval(() => {

        console.log("Waiting for tokenization response...");

        // wait for tokenization response...
        if (that.paymentMethodId) {
          that.selectedPaymentMethod = PaymentMethod.createEmpty();
          that.selectedPaymentMethod.id = that.paymentMethodId;

          //If we are doing ACH we need to add the paymentmethod type
          if (this.selectedMode == this.MODE_ECHECK) {
            that.selectedPaymentMethod.cardType = 'Other';
          }

          if (that.tokenizeInterval) {
            window.clearInterval(that.tokenizeInterval);
          }

          console.log("Submitting transaction...");

          that
            ._submitTransaction()
            .then((response) => {
              this.transactionResponse = response;
              this.formIsBeingSubmitted = false;
            })
            .catch((message) => {
              this.loadingSpinnerRef?.close();
              this.uiErrorHandler.handleError(message);
              that.formIsBeingSubmitted = false;
            }).finally(() => {
              this.formIsBeingSubmitted = false;
              this.loadingSpinnerRef?.close();
            })
        }
      }, 1000);
    } else {
      this._submitTransaction()
        .then((response) => {
          this.loadingSpinnerRef?.close();
          this.formIsBeingSubmitted = false;

          if (response) {
            if (!response.success) {
              this.loadingSpinnerRef?.close();
              this.uiErrorHandler.handleError(
                'The transaction could not be processed: ' + response.error,
              );
              return;
            }
          } else {
            this.loadingSpinnerRef?.close();
            this.uiErrorHandler.handleError(
              'An error occurred while processing this transaction. Please try again later.',
            );
            this.formIsBeingSubmitted = false;
            return;
          }

          this.transactionResponse = response;
        })
        .catch((ex) => {
          this.loadingSpinnerRef?.close();
          this.uiErrorHandler.handleError(
            'The transaction could not be processed: ' + ex,
          );
          this.formIsBeingSubmitted = false;
        }).finally(() => {
          this.formIsBeingSubmitted = false;
          this.loadingSpinnerRef?.close();
        })
    }
  }

  getTransactionTypeVerb(transType: number): string {
    transType = parseInt(transType.toString());

    switch (transType) {
      case TransactionType.TRANSACTION_TYPE_AUTH:
        return TransactionTypeVerb.TRANSACTION_TYPE_AUTH_VERB;
      case TransactionType.TRANSACTION_TYPE_AUTHCAPTURE:
        return TransactionTypeVerb.TRANSACTION_TYPE_AUTHCAPTURE_VERB;
      case TransactionType.TRANSACTION_TYPE_CAPTURE:
        return TransactionTypeVerb.TRANSACTION_TYPE_CAPTURE_VERB;
      case TransactionType.TRANSACTION_TYPE_REFUND:
        return TransactionTypeVerb.TRANSACTION_TYPE_REFUND_VERB;
      case TransactionType.TRANSACTION_TYPE_REVERSAL:
        return TransactionTypeVerb.TRANSACTION_TYPE_REVERSAL_VERB;
      case TransactionType.TRANSACTION_TYPE_RECUR:
        return TransactionTypeVerb.TRANSACTION_TYPE_RECUR_VERB;
      case TransactionType.TRANSACTION_TYPE_VALIDATE:
        return TransactionTypeVerb.TRANSACTION_TYPE_VALIDATE_VERB;
      case TransactionType.TRANSACTION_TYPE_VOID:
        return TransactionTypeVerb.TRANSACTION_TYPE_VOID_VERB;
    }

    throw 'unrecognized transaction type [' + transType + ']';
  }

  async _submitTransaction(): Promise<TransactionResponse> {
    if (this.orderDetails.transactionType) {
      this.updateTransactionTypeFriendlyName(this.orderDetails.transactionType);
    } else {
      throw 'transaction type not selected';
    }

    this.formIsBeingSubmitted = true;

    if (this.selectedPaymentMethod) {
      if (this.amountToTransact == undefined) {
        throw new Error('enter an amount to charge');
      } else if (
        this.selectedPaymentMethod == undefined ||
        (this.selectedPaymentMethod != undefined &&
          !this.selectedPaymentMethod.id)
      ) {
        throw new Error('select a payment method');
      } else if (this.orderDetails.transactionType < 0) {
        throw new Error('select a transaction type');
      } else if (
        this.selectedCustomer == undefined ||
        this.selectedCustomer.id == undefined
      ) {
        throw new Error('select a customer');
      } else if (this.orderDetails.transactionType < 0) {
        throw new Error('enter a valid amount');
      }

      let totalAmount = Math.round(
        this.amountToTransact * TerminalComponent.MAGNIFIER,
      );

      let totalTaxAmount = 0;
      if (this.orderDetails.L2L3Data && this.orderDetails.L2L3Data.taxes) {
        totalTaxAmount = this.orderDetails.L2L3Data.taxes.reduce(
          (acc, tax) => acc + (tax.amount || 0),
          0,
        );
      }

      this.orderDetails.L2L3Items = [new L2L3Item({
        quantity: this.orderDetails.L2L3Data?.quantity,
        unitOfMeasure: this.orderDetails.L2L3Data?.unitOfMeasure || 'ITM',
        productCode: this.orderDetails.L2L3Data?.productCode,
        unitPrice: totalAmount - totalTaxAmount,
      })];

      let transRequest: TerminalTransactionRequest =
        new TerminalTransactionRequest({
          paymentMethodId: this.selectedPaymentMethod.id,
          customerId: this.selectedCustomer?.id,
          verb: this.getTransactionTypeVerb(this.orderDetails.transactionType),
          magnifiedAmount: totalAmount,
          currencyCode: TerminalComponent.CURRENCY_CODE,
          billingAddress: this.selectedBillingAddress,
          shippingAddress: this.shippingAddress,
          description: this.orderDetails.description,
          recipientEmail:
            this.orderDetails.recipientEmail ||
            this.selectedCustomer?.emailAddress,
          quantity: this.orderDetails.L2L3Data?.quantity,
          unitOfMeasure: this.orderDetails.L2L3Data?.unitOfMeasure || 'ITM',
          productCode: this.orderDetails.L2L3Data?.productCode,
          ShippingAndHandlingAmount:
            this.orderDetails.L2L3Data?.shippingAndHandlingAmount || 0,
          taxes: this.orderDetails.L2L3Data?.taxes || [],
          DutyAmount: this.orderDetails.L2L3Data?.dutyAmount || 0,
          taxAmount: totalTaxAmount,
          invoiceNumber: this.orderDetails.invoiceNumber,
          poNumber: this.orderDetails.poNumber,
          items: this.orderDetails.L2L3Items || [],
        });

      let response: TransactionResponse | undefined;

      if (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTHCAPTURE ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTH
      ) {
        transRequest.billingAddress = this.selectedBillingAddress;
        transRequest.shippingAddress = this.shippingAddress;
        if (this.selectedPaymentMethod.cardType == 'Other') {
          if (this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTH) {
            this.uiErrorHandler.handleError(
              'ACH transactions are not supported for authorization transactions',
            );
          } else {
            response = await this.transactionService.sendACHTransaction(transRequest);
          }
        } else {
          response = await this.transactionService.sendGatewayTransaction(transRequest);
        }
      } else if (this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_RECUR) {
        transRequest.isRecurring = true;

        const recurringSchedule = this.orderDetails.recurringSchedule;
        if (!recurringSchedule) {
          throw 'recurring schedule not found';
        }

        let dt = new Date(
          recurringSchedule.firstChargeDate?.toString() || '',
        );
        if (dt) {
          recurringSchedule.firstChargeDateISO = dt.toISOString();
        } else {
          throw 'the start date is not valid';
        }

        if (recurringSchedule.finalChargeDate) {
          dt = new Date(
            recurringSchedule.finalChargeDate?.toString() || '',
          );
          if (dt) {
            recurringSchedule.finalChargeDateISO = dt.toISOString();
          } else {
            throw 'the end date is not valid';
          }
        }

        if (recurringSchedule.periodCount) {
          recurringSchedule.periodCount = parseInt(
            recurringSchedule.periodCount?.toString(),
          );
        }
        if (recurringSchedule.numberOfCharges) {
          recurringSchedule.numberOfCharges = parseInt(
            recurringSchedule.numberOfCharges?.toString(),
          );
        }

        transRequest.recurringSchedule = recurringSchedule;

        if (this.selectedPaymentMethod.cardType == 'Other') {
          response = await this.transactionService.sendACHTransaction(transRequest);
        } else {
          response = await this.transactionService.sendGatewayTransaction(transRequest);
        }
      } else if (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_REFUND ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_VOID
      ) {
        if (this.selectedTransaction?.id) {
          transRequest.originalTransactionId = this.selectedTransaction.id;
          transRequest.orderId = this.selectedTransaction.orderId;

          if (this.selectedPaymentMethod.cardType == 'Other') {
            response = await this.transactionService.sendACHTransaction(transRequest);
          } else {
            response = await
              this.transactionService.sendGatewayTransaction(transRequest);
          }
        } else {
          throw 'select a transaction first';
        }
      } else if (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_CAPTURE
      ) {
        if (this.selectedPaymentMethod.cardType == 'Other') {
          this.uiErrorHandler.handleError(
            'ACH transactions are not supported for capture transactions',
          );
        };
        if (this.selectedTransaction?.id) {
          transRequest.originalTransactionId = this.selectedTransaction.id;
          transRequest.orderId = this.selectedTransaction.orderId;

          
          response = await
            this.transactionService.sendGatewayTransaction(transRequest);
        } else {
          throw 'select a transaction first';
        }
      }
      //else if (this.selectedTransactionType == this.TRANSACTION_TYPE_VALIDATE) {
      //    transactionResponse = this.transactionService.sendVerify(trans);
      //}
      else {
        throw (
          'unrecognized transaction type [' + this.orderDetails.transactionType + ']'
        );
      }

      this.formIsBeingSubmitted = false;
      if (response) {
        return response;
      } else {
        throw 'an error occurred while processing the transaction';
      }
    } else {
      throw 'The payment method is not valid';
    }
    
  }

  updateTransactionTypeFriendlyName(transType: TransactionType) {

    switch (transType) {
      case TransactionType.TRANSACTION_TYPE_AUTH:
        this.selectedTransactionFriendlyName = 'Authorization';
        break;
      case TransactionType.TRANSACTION_TYPE_AUTHCAPTURE:
        this.selectedTransactionFriendlyName = 'Sale';
        break;
      case TransactionType.TRANSACTION_TYPE_CAPTURE:
        this.selectedTransactionFriendlyName = 'Capture';
        break;
      case TransactionType.TRANSACTION_TYPE_RECUR:
        this.selectedTransactionFriendlyName = 'Recurring charge';
        break;
      case TransactionType.TRANSACTION_TYPE_REFUND:
        this.selectedTransactionFriendlyName = 'Refund';
        break;
      case TransactionType.TRANSACTION_TYPE_REVERSAL:
        this.selectedTransactionFriendlyName = 'Reversal';
        break;
      case TransactionType.TRANSACTION_TYPE_VOID:
        this.selectedTransactionFriendlyName = 'Void';
        break;
      default:
        'transaction';
    }
  }

  selectTransType_click(transType: TransactionType): void {

    this.updateTransactionTypeFriendlyName(transType);

    if (this.selectedTransaction !== undefined) {
      // The selected transaction may not be applicable to the newly selected
      // transaction type, so we'll clear out all previous selections.
      this.clearAllSelections();
    }
  }

  get PaneEnum(): typeof PaneEnum {
    return PaneEnum;
  }

  // TODO: fix internationalization
  formatCurrency(magnifiedTotalAmount: number, magnifier: number) {
    return TransactionFacade.getFormattedAmount(
      magnifiedTotalAmount,
      magnifier,
      'USD',
    );
  }

  allowModeTypeSelection(): boolean {
    return this.selectedMode == undefined;
  }

  allowModeTypeChange(): boolean {
    return this.selectedMode != undefined;
  }

  allowTransactionTypeChange(): boolean {
    return true; //this.orderDetails.transactionType < 0;
  }

  clearAllSelections() {
    this.orderId = undefined;
    //this.orderDetails = OrderDetails.createEmpty();
    this.orderDetailsForm?.clear();
    this.L2L3Form?.clear();

    this.selectedCustomer = undefined;
    this.customerForm?.clear();

    this.selectedBillingAddress = StreetAddress.createEmpty();
    this.shippingAddress = StreetAddress.createEmpty();
    this.selectedPaymentMethod = undefined;
    this.paymentMethodId = undefined;
    this.billingForm?.refresh();
    this.billingForm?.clear();

    this.amountToTransact = undefined;
    // this.amountToTransactFormatted = '';

    this.selectedTransaction = undefined;
    this.allowTransactionTypeSelection = true;

    this.transactionsForm?.clearSelection();

    this.orderDetails.recurringSchedule = undefined;

    this.transactionResponse = undefined;

    this.checkIfFormIsComplete();
  }

  showPane(pane: PaneEnum): boolean {
    if (pane == PaneEnum.Product) {
      //return this.selectedTransactionType != this.TRANSACTION_TYPE_REFUND;
    } else if (pane == PaneEnum.Customer) {
      return (
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_REFUND &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VOID &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_CAPTURE
      );
    } else if (pane == PaneEnum.Transactions) {
      return (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_REFUND ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_CAPTURE ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_VOID
      );
    } else if (pane == PaneEnum.Billing) {
      return (
        this.selectedCustomer != undefined &&  
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VOID &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_REFUND
      );
    } else if (pane == PaneEnum.OrderDetails) {
      return (
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_REFUND &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VALIDATE &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VOID
      );
    } else if (pane == PaneEnum.L2L3) {
      return (
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_REFUND &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VALIDATE &&
        this.orderDetails.transactionType != TransactionType.TRANSACTION_TYPE_VOID
      );
    } else if (pane == PaneEnum.Shipping) {
      return (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTHCAPTURE ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_RECUR
      );
    } else if (pane == PaneEnum.RecurringPayment) {
      return this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_RECUR;
    } else if (pane == PaneEnum.Summary) {
      return (
        (this.orderDetails.transactionType == undefined &&
          this.selectedMode == undefined) ||
        this.transactionResponse != undefined
      );
    } else if (pane == PaneEnum.Refund) {
      return (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_REFUND
      );
    }

    return true;
  }

  getPaymentMethodFromId(id: string): PaymentMethod | undefined {
    if (
      this.selectedCustomer != undefined &&
      this.selectedCustomer.paymentMethods != null
    ) {
      for (var i = 0; i < this.selectedCustomer.paymentMethods.length; i++) {
        if (this.selectedCustomer.paymentMethods[i].id == id) {
          return this.selectedCustomer.paymentMethods[i];
        }
      }
    }

    return undefined;
  }

  getPaymentMethodName(): string {
    let html = '';

    if (this.selectedPaymentMethod != undefined) {
      if (this.selectedPaymentMethod.cardFirst6) {
        try {
          html += this.uihelper.getCardName(
            this.selectedPaymentMethod.cardFirst6,
          );
        } catch {}
      }
      html += '<br/>';

      if (this.selectedPaymentMethod.cardLast4 != '') {
        html += '****' + this.selectedPaymentMethod.cardLast4;
      }

      if (
        this.selectedPaymentMethod.cardExpMonth &&
        this.selectedPaymentMethod.cardExpYear
      ) {
        html +=
          '<br/>' +
          this.selectedPaymentMethod.cardExpMonth +
          '/' +
          this.selectedPaymentMethod.cardExpYear;
      }
    }

    return html;
  }

  /* TODO: Can a merchant submit multiple refunds on a transaction?
    computeMaximumRefundAmount(): number {
        if (!this.selectedTransaction || !this.selectedTransaction.magnifiedTotalAmount || !this.selectedTransaction?.magnifier) {
            throw "select a transaction first"
        }
        let total = this.selectedTransaction.magnifiedTotalAmount / this.selectedTransaction.magnifier;

        if (this.subsequentTransactions != undefined) {
            for (var i=0; i<this.subsequentTransactions.length; i++) {
                if (this.subsequentTransactions[i].transactionType == "REFUND") {
                    total -= this.subsequentTransactions[i].amount;
                }
            }
        }

        return total;
    }
    */

  // Validation

  checkIfFormIsComplete() {
    this.formIsComplete = true;

    if (this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_VOID) {
      this.formIsComplete =
        this.selectedMode != undefined && this.selectedTransaction != undefined;
    } else if (this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_REFUND) {
      this.formIsComplete =
        this.selectedMode != undefined &&
        this.selectedTransaction != undefined &&
        this.amountToTransact != undefined &&
        this.amountToTransact <= this.maxRefundAmount! &&
        this.amountToTransact > 0;
    } else {
      if (!this.amountToTransact || this.amountToTransact <= 0) {
        this.formIsComplete = false;
      } else if (
        this.selectedCustomer == undefined &&
        !this.paymentFormPassesValidation
      ) {
        this.formIsComplete = false;
      } else if (
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTHCAPTURE ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_AUTH ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_VALIDATE ||
        this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_CAPTURE
      ) {
        this.formIsComplete =
          this.selectedMode != undefined &&
          (this.selectedPaymentMethod != undefined ||
            this.paymentFormPassesValidation);

      } else if (this.orderDetails.transactionType == TransactionType.TRANSACTION_TYPE_RECUR) {
        const recurringSchedule = this.orderDetails.recurringSchedule;

        this.formIsComplete =
          this.selectedMode != undefined &&
          (this.selectedPaymentMethod != undefined ||
            this.paymentFormPassesValidation) &&
          recurringSchedule != undefined &&
          recurringSchedule.firstChargeDate != undefined &&
          recurringSchedule.period != undefined &&
          recurringSchedule.periodCount != undefined &&
          recurringSchedule.chargeUntil != undefined &&
          (recurringSchedule.chargeUntil == 'cancelled' ||
            (recurringSchedule.chargeUntil == 'date' &&
              recurringSchedule.finalChargeDate != undefined) ||
            (recurringSchedule.chargeUntil == 'number' &&
              recurringSchedule.numberOfCharges != undefined));
      } else {
        throw (
          'transaction type [' +
          this.orderDetails.transactionType +
          '] not supported'
        );
      }
    }
  }
}
